"use client";

import type React from "react";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import InfoIcon from "@/assets/images/help-circle.svg";
import CloseIcon from "@/assets/images/toast-close.svg";
import ErrorIcon from "@/assets/images/toast-error.svg";
import SuccessIcon from "@/assets/images/toast-success.svg";
import WarningIcon from "@/assets/images/toast-warning.svg";

type AlertType = "success" | "error" | "warning" | "info";

interface Alert {
  id: string;
  type: AlertType;
  message: string;
  duration?: number;
}

interface AlertContextType {
  alerts: Alert[];
  showAlert: (type: AlertType, message: string, duration?: number) => void;
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
  showWarning: (message: string) => void;
  showInfo: (message: string) => void;
  removeAlert: (id: string) => void;
}

const AlertContext = createContext<AlertContextType>({
  alerts: [],
  showAlert: () => {},
  showSuccess: () => {},
  showError: () => {},
  showWarning: () => {},
  showInfo: () => {},
  removeAlert: () => {},
});

export const useAlert = () => useContext(AlertContext);

const TYPE_CONFIG: Record<
  AlertType,
  {
    label: string;
    gradient: string;
    iconBorder: string;
    Icon: React.ComponentType<{ className?: string }>;
  }
> = {
  success: {
    label: "Success",
    gradient: "var(--gradient-toast-success)",
    iconBorder: "border-success-900",
    Icon: SuccessIcon,
  },
  error: {
    label: "Error",
    gradient: "var(--gradient-toast-error)",
    iconBorder: "border-error-900",
    Icon: ErrorIcon,
  },
  warning: {
    label: "Warning",
    gradient: "var(--gradient-toast-warning)",
    iconBorder: "border-warning-900",
    Icon: WarningIcon,
  },
  info: {
    label: "Info",
    gradient: "var(--gradient-toast-info)",
    iconBorder: "border-info-900",
    Icon: InfoIcon,
  },
};

export function AlertProvider({ children }: { children: React.ReactNode }) {
  const [alerts, setAlerts] = useState<Alert[]>([]);

  const removeAlert = useCallback((id: string) => {
    setAlerts((prev) => prev.filter((a) => a.id !== id));
  }, []);

  const showAlert = useCallback(
    (type: AlertType, message: string, duration = 5000) => {
      const id =
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random()}`;
      setAlerts((prev) => [...prev, { id, type, message, duration }]);
      if (duration > 0) {
        setTimeout(() => removeAlert(id), duration);
      }
    },
    [removeAlert],
  );

  const showSuccess = useCallback(
    (message: string) => showAlert("success", message),
    [showAlert],
  );
  const showError = useCallback(
    (message: string) => showAlert("error", message),
    [showAlert],
  );
  const showWarning = useCallback(
    (message: string) => showAlert("warning", message),
    [showAlert],
  );
  const showInfo = useCallback(
    (message: string) => showAlert("info", message),
    [showAlert],
  );

  const value = useMemo(
    () => ({
      alerts,
      showAlert,
      showSuccess,
      showError,
      showWarning,
      showInfo,
      removeAlert,
    }),
    [
      alerts,
      showAlert,
      showSuccess,
      showError,
      showWarning,
      showInfo,
      removeAlert,
    ],
  );

  return (
    <AlertContext.Provider value={value}>
      {children}
      {/* Toast renderer — fixed top-right, dismissible, auto-stacked.

          **The top of the stacking order, deliberately.** This app runs three
          layers above Tailwind's scale: `CModal` sets `999999` on the backdrop
          and `1000000` on its panel (inline styles, so no utility outranks
          them), and popovers opened from inside a modal sit at `z-1000001`.
          A toast has to clear all three or it fires behind whatever raised it —
          at the old `z-[200]` every confirmation from inside a dialog was
          invisible. Nothing may be given a higher z-index than this. */}
      <div
        aria-live="polite"
        className="pointer-events-none fixed top-4 right-4 z-1000002 flex max-w-150 flex-col items-end gap-2"
      >
        {alerts.map((alert) => {
          const { label, gradient, iconBorder, Icon } = TYPE_CONFIG[alert.type];
          return (
            <div
              key={alert.id}
              style={{ backgroundImage: gradient }}
              className="pointer-events-auto flex w-fit min-w-80 max-w-full items-start justify-between gap-6 rounded-lg border border-white py-3.5 pr-2.5 pl-4 shadow-toast"
            >
              <div className="flex min-w-0 items-center gap-2.5">
                <div
                  className={`flex size-8 shrink-0 items-center justify-center rounded-2xl border bg-white/20 ${iconBorder}`}
                >
                  <Icon className="size-4 text-white" />
                </div>
                <div className="flex min-w-0 flex-col gap-0.5">
                  <p className="label-md whitespace-nowrap text-text-white">
                    {label}
                  </p>
                  <p className="paragraph-xs truncate whitespace-nowrap text-text-disabled">
                    {alert.message}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => removeAlert(alert.id)}
                className="mt-0.5 shrink-0"
                aria-label="Dismiss"
              >
                <CloseIcon className="size-5" />
              </button>
            </div>
          );
        })}
      </div>
    </AlertContext.Provider>
  );
}
